/**
 * The bar over Home's center pane: the Briefing / Dashboard / By Type
 * segment always in the middle, the title or hint on the left, and Mine /
 * Team and (in By Type) the Vertical / Horizontal toggle and Display on the
 * right.
 */
import dayjs from "dayjs";
import { Sparkles } from "lucide-react";
import type { FC, ReactNode } from "react";

import { LayoutToggle, type HomeLayout } from "@/components/ui/layout-toggle";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { HomeScope, HomeView } from "../dashboardLayoutStore";
import { briefingTitle } from "../greeting";
import { HomeDisplayMenu } from "./HomeDisplayMenu";
import { HomeHeaderRow } from "./HomeHeaderRow";

const VIEWS: { value: HomeView; label: string }[] = [
  { value: "briefing", label: "Briefing" },
  { value: "dashboard", label: "Dashboard" },
  { value: "byType", label: "By Type" }
];

export const HomeViewTabs: FC<{
  view: HomeView;
  onViewChange: (view: HomeView) => void;
}> = ({ view, onViewChange }) => (
  <Tabs value={view} onValueChange={(v) => onViewChange(v as HomeView)}>
    <TabsList aria-label="Home View" data-testid="home-view-tabs">
      {VIEWS.map((v) => (
        <TabsTrigger key={v.value} value={v.value}>
          {v.label}
        </TabsTrigger>
      ))}
    </TabsList>
  </Tabs>
);

export const HomeScopeTabs: FC<{
  scope: HomeScope;
  onScopeChange: (scope: HomeScope) => void;
}> = ({ scope, onScopeChange }) => (
  <Tabs value={scope} onValueChange={(v) => onScopeChange(v as HomeScope)}>
    <TabsList aria-label="Scope" data-testid="home-scope">
      <TabsTrigger value="mine">Mine</TabsTrigger>
      <TabsTrigger value="team">Team</TabsTrigger>
    </TabsList>
  </Tabs>
);

type Props = {
  view: HomeView;
  onViewChange: (view: HomeView) => void;
  scope: HomeScope;
  onScopeChange: (scope: HomeScope) => void;
  layout: HomeLayout;
  onLayoutChange: (layout: HomeLayout) => void;
  /** A quiet line in By Type ("Hidden: Calendar · Deadlines"). */
  hint?: string;
  /** Extra controls before the scope (the briefing's prev / next arrows). */
  children?: ReactNode;
  trailing?: ReactNode;
};

export const HomeViewBar: FC<Props> = ({
  view,
  onViewChange,
  scope,
  onScopeChange,
  layout,
  onLayoutChange,
  hint,
  children,
  trailing
}) => (
  <div
    className="flex min-h-10 shrink-0 items-center border-b border-border px-3 py-1.5"
    data-testid="pane-bar"
  >
    <HomeHeaderRow
      start={
        view === "briefing" ? (
          <>
            <Sparkles className="size-3.5 shrink-0 text-fg-3" />
            <span className="min-w-0 truncate text-base text-fg">
              {briefingTitle()}
            </span>
            <span className="truncate text-base text-fg-3">
              · {dayjs().format("dddd, MMMM D")}
            </span>
          </>
        ) : view === "byType" && hint ? (
          <span
            className="hidden truncate text-sm text-fg-3 @2xl:inline"
            data-testid="home-hint"
          >
            {hint}
          </span>
        ) : null
      }
      center={<HomeViewTabs view={view} onViewChange={onViewChange} />}
      end={
        <>
          {children}
          {view === "briefing" ? null : (
            <HomeScopeTabs scope={scope} onScopeChange={onScopeChange} />
          )}
          {view === "byType" ? (
            <>
              <LayoutToggle value={layout} onValueChange={onLayoutChange} />
              <HomeDisplayMenu view={view} />
            </>
          ) : null}
          {trailing}
        </>
      }
    />
  </div>
);
